import { Formiz, useForm } from "@formiz/core";
import { isMaxLength, isMinLength, isPattern, isRequired } from "@formiz/validations";
import { IProfile, setProfile, useAppDispatch, useUserInfo } from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { Icon, IconSize, IconVariants } from "@next-platform/client-common-spicy-ui";
import { Spacer, Spinner } from "@nextui-org/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";

import FormField from "@/components/common/FormField";
import PageStaticMetaTags from "@/components/common/PageStaticMetaTags";
import { Grid, SpicyButton, SpicyInput, Text } from "@/components/customComponents";
import useApi from "@/components/useApi";
import { EXTERNAL_USERNAME_KEY, HALF_A_SECOND } from "@/constants";
import useAuth from "@/services/useAuth";

const logger = appLogger.withTag("ExternalSignUp");

const DEBOUNCE_USERNAME_LENGTH = 4;
const USERNAME_MAX_LENGTH = 20;

const usernameValidations = [
  {
    rule: isRequired(),
    message: "Username can't be empty",
  },
  {
    rule: isMinLength(DEBOUNCE_USERNAME_LENGTH),
    message: "Username is too short, it should be at least 4 characters long.",
  },
  {
    rule: isMaxLength(USERNAME_MAX_LENGTH),
    message: "Username should be less then 20 characters ",
  },
  {
    rule: isPattern("^[a-zA-Z0-9_-]*$"),
    message: "Username contain invalid characters",
  },
];

const nameValidations = [
  {
    rule: isRequired,
    message: "Name can't be empty",
  },
  {
    rule: isMinLength(DEBOUNCE_USERNAME_LENGTH),
    message: "Name is too short, it should be at least 4 characters long.",
  },
];

const ExternalSignUp = () => {
  const userForm = useForm();
  const { invalidateFields } = userForm;
  const { isLoading: isAuthLoading, login } = useAuth();
  const navigate = useNavigate();
  const { jsonApiService, callApi, isLoading: isSubmitting, errorData } = useApi<{ isAvailable: boolean }>();
  const dispatch = useAppDispatch();
  const { username: profileUsername, name: profileName } = useUserInfo();
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [isUsernameAvailable, setIsUsernameAvailable] = useState(false);
  const [debouncedUsername] = useDebounce(userForm.values?.username, HALF_A_SECOND);
  useEffect(() => {
    const checkUsernameAvailability = async (username: string) => {
      try {
        await callApi(jsonApiService.checkUsername, username).then((result) => {
          const { isAvailable } = result ?? {};
          if (!isAvailable) {
            invalidateFields({ username: "Username is not available" });
          }

          setIsUsernameAvailable(!!isAvailable);
          setIsCheckingUsername(false);
        });
      } catch (error) {
        logger.error("Error in checkUsernameAvailability", error);
        // Handle error
      }
    };
    if (debouncedUsername && debouncedUsername.length >= DEBOUNCE_USERNAME_LENGTH) {
      setIsCheckingUsername(true);
      checkUsernameAvailability(debouncedUsername);
    }
  }, [debouncedUsername, callApi, jsonApiService.checkUsername, invalidateFields]);

  const usernameHelper = useMemo(() => {
    if (!userForm.values.username) {
      return {
        text: "",
        color: "",
      };
    }

    if (userForm.values.username.length < DEBOUNCE_USERNAME_LENGTH) {
      return {
        text: "Username is invalid",
        color: "error",
      };
    }
    const isValid = userForm.values.username.match(/^[a-zA-Z0-9_-]*$/);

    if (!isValid) {
      return {
        text: isValid ? "Valid" : "Username is invalid",
        color: isValid ? "success" : "error",
      };
    }
    if (isCheckingUsername) {
      return {
        text: "Checking...",
        color: "info",
        icon: <Spinner size="sm" />,
      };
    }

    if (isUsernameAvailable) {
      return {
        text: "Available",
        color: "",
        icon: <Icon icon={IconVariants.CHECK_CIRCLE} className="text-green-600" size={IconSize.SMALL} />,
      };
    }
    return {};
  }, [userForm.values.username, isCheckingUsername, isUsernameAvailable]);

  const createUser = async () => {
    const result = (await callApi(jsonApiService.createUserExternal, userForm.values.name, userForm.values.username)) as unknown as {
      token: string;
      user: IProfile;
    };
    if (result?.user) {
      // At this step we have done all steps to "onboard" the external user, and they are now yet another user
      // with a different set of permissions.
      await login({ isPartner: true, token: result.token, app_state: null });
      dispatch(setProfile(result.user));
      navigate("/");
    }
  };

  const submitForm = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      createUser();
    },
    [createUser],
  );

  const externalUsername = localStorage.getItem(EXTERNAL_USERNAME_KEY);
  const memoizedInitialValues = useMemo(
    () => ({ username: externalUsername ?? profileUsername, name: externalUsername ?? profileName }),
    [externalUsername, profileUsername, profileName],
  );

  if (isAuthLoading) {
    return <Spinner />;
  }

  return (
    <Grid className="p-[10px] h-[80vh] flex flex-col justify-center items-center">
      <PageStaticMetaTags pageId="signup" />
      <>
        <span className="mb-2.5 tracking-tighter font-semibold text-2xl mob:text-4xl">We'll get you chatting in no time!</span>
        <span className="font-semibold tracking-tight text-base mob:text-xl">Please fill the following details to setup your profile</span>
      </>

      <Spacer y={3} />

      <Formiz onValidSubmit={submitForm} connect={userForm} initialValues={memoizedInitialValues}>
        <form onSubmit={submitForm}>
          <FormField
            component={SpicyInput}
            variant="primary_small_short"
            color="primary"
            title="Username"
            name="username"
            desc="Your username must be unique and at least 4 characters long."
            aria-labelledby="username"
            isClearable
            spacer={5}
            maxLength={20}
            endContent={usernameHelper.icon}
            validations={usernameValidations}
          />
          <FormField
            component={SpicyInput}
            variant="primary_small_short"
            color="primary"
            title="Name"
            name="name"
            desc="How you want to be called when chatting and at least 4 characters long"
            aria-labelledby="name"
            maxLength={USERNAME_MAX_LENGTH}
            validations={nameValidations}
          />

          <div className="text-green-600 text-left ml-4 p-[2.5] h-[35px]">
            {errorData.message ? <Text type={"error"}>{errorData.message}</Text> : null}
          </div>
          <SpicyButton
            type="submit"
            aria-labelledby="save"
            isDisabled={!userForm.isValid || isSubmitting}
            variant="primary_large"
            color={!userForm.isValid || isSubmitting ? "disabled" : "primary_strong"}
          >
            {isSubmitting ? <Spinner color="current" size="sm" /> : "Start Chatting"}
          </SpicyButton>
        </form>
      </Formiz>
    </Grid>
  );
};

export default ExternalSignUp;
